export type InsightTier = 1 | 2 | 3 | 4;

// Colors sampled from the insight gradient bar (top=blue → mid=purple → bottom=coral).
// Used as the single source of truth for tier colors across the My Work page.
export const TIER_COLORS: Record<
    InsightTier,
    {
        chip: string;
        dot: string;
        border: string;
        headerBg: string;
        badge: string;
        bar: string;
    }
> = {
    1: {
        chip: "bg-blue-100 text-blue-700",
        dot: "#14C8EB",
        border: "border-blue-200",
        headerBg: "bg-gradient-to-r from-blue-50 to-white",
        badge: "bg-blue-500",
        bar: "bg-gradient-to-b from-blue-400 to-blue-200",
    },
    2: {
        chip: "bg-purple-100 text-purple-700",
        dot: "#D2D2FF",
        border: "border-purple-200",
        headerBg: "bg-gradient-to-r from-purple-50 to-white",
        badge: "bg-purple-500",
        bar: "bg-gradient-to-b from-purple-400 to-purple-200",
    },
    3: {
        chip: "bg-[#FFD3A8] text-amber-800",
        dot: "#FFD3A8",
        border: "border-amber-200",
        headerBg: "bg-gradient-to-r from-amber-50 to-white",
        badge: "bg-amber-500",
        bar: "bg-gradient-to-b from-amber-400 to-amber-200",
    },
    4: {
        chip: "bg-emerald-100 text-emerald-700",
        dot: "#34D399",
        border: "border-emerald-200",
        headerBg: "bg-gradient-to-r from-emerald-50 to-white",
        badge: "bg-emerald-500",
        bar: "bg-gradient-to-b from-emerald-400 to-emerald-200",
    },
};

export const TIER_CHIP_CLASSES: Record<InsightTier, string> = Object.fromEntries(Object.entries(TIER_COLORS).map(([k, v]) => [k, v.chip])) as Record<InsightTier, string>;

// Maps the LLM-assigned experiment_slug to a data-depth tier (drives pill color + "sources used"):
//   tier 1 = calendar only, tier 2 = + perspective, tier 3 = + collaboration profile, tier 4 = Work IQ.
// experiment_slug is uncontrolled LLM output, so unmapped/newly-invented slugs fall back to DEFAULT_INSIGHT_TIER.
export const SLUG_TIER_MAP: Record<string, InsightTier> = {
    // tier 1 — calendar / structure
    calendar_insights: 1,
    meeting_effectiveness_score: 1,
    optimal_one_on_one_timing: 1,
    energy_depletion_risk: 1,
    stress_pattern_detected: 1,
    structure_mismatch: 1,
    // tier 2 — + perspective
    personality_calendar_misalignment: 2,
    decision_making_mode_awareness: 2,
    relationship_gap: 2,
    one_on_one_coaching: 2,
    manager_alert: 2,
    post_event_check_in: 2,
    // tier 3 — + collaboration profile
    initiative_insights: 3,
    team_dynamic_risk: 3,
    cross_functional_collaboration_alert: 3,
    momentum_risk: 3,
    meeting_dynamics_preview: 3,
    pre_meeting_alignment: 3,
    post_meeting_debrief: 3,
    potential_friction: 3,
    collaboration_insights: 3,
    // Seeded demo experiment slugs (seed_demo_insights.py), shown only when CALENDAR_INTEGRATION_DEMO_DATA is on.
    // These are hyphen-separated, unlike the underscore-separated production slugs above.
    "manager-alert": 2,
    "high-stakes-meeting-prep": 1,
    "calendar-mismatch": 1,
    "project-risk-radar": 3,
    "team-tension": 3,
    // tier 4 — Work IQ (Microsoft 365 signal pipeline, PLT-1450)
    workiq_insights: 4,
};

export const DEFAULT_INSIGHT_TIER: InsightTier = 1;

export const TIER_LABELS: Record<InsightTier, string> = {
    1: "Calendar",
    2: "Perspective",
    3: "Collaboration Profile",
    4: "Work IQ",
};

export interface InsightSourceFeatureFlags {
    calendarEnabled?: boolean;
    perspectiveEnabled?: boolean;
    collaborationProfileEnabled?: boolean;
    workIqEnabled?: boolean;
    perspectivePresent?: boolean;
    collaborationProfilePresent?: boolean;
    workIqPresent?: boolean;
}

export function getInsightSourcesUsed(tier: InsightTier, flags: InsightSourceFeatureFlags = {}): string[] {
    const enabledByTier: Record<InsightTier, boolean> = {
        1: !!flags.calendarEnabled,
        2: !!flags.perspectiveEnabled && !!flags.perspectivePresent,
        3: !!flags.collaborationProfileEnabled && !!flags.collaborationProfilePresent,
        4: !!flags.workIqEnabled && !!flags.workIqPresent,
    };

    const sourceTiers: readonly InsightTier[] = tier === 4 ? [4] : ([1, 2, 3] as const).slice(0, tier);

    return sourceTiers.filter((sourceTier) => enabledByTier[sourceTier]).map((sourceTier) => TIER_LABELS[sourceTier]);
}

export const MYWORK_CTA_SOURCES = {
    ACTIVATOR: "activator",
    PREVIEW_CARD: "preview_card",
    PERSPECTIVE_NUDGE_CARD: "perspective_nudge_card",
} as const;

export function tierForExperimentSlug(slug: string | null | undefined): InsightTier {
    if (!slug) {
        return DEFAULT_INSIGHT_TIER;
    }
    return SLUG_TIER_MAP[slug] ?? DEFAULT_INSIGHT_TIER;
}
